import { de, formatDate } from '../../i18n'
import type { Setting, WriteSettingBody } from './types'

/**
 * §8's table cell for "Wert": never the actual secret value (design
 * §8.1 — a listing reports only that one exists), and "nicht gesetzt" for
 * anything not currently in effect — which, per §8.2, is deliberately
 * distinct from a stored empty value in the design's own model, though
 * `listSettings` (service.ts) cannot tell the two apart on the wire today:
 * both arrive as `hasValue: false`. That is an existing API simplification,
 * not something this view invents.
 */
export function settingValueDisplay(setting: Setting): string {
  if (setting.isSecret) return setting.hasValue ? de.settings.value.stored : de.settings.value.unset
  return setting.hasValue ? setting.value : de.settings.value.unset
}

/**
 * The currently-resolved row's validity window, or `null` for a setting
 * with no window on either end right now — the table (SettingsTable.tsx)
 * renders this only when it is non-null, never as an always-empty column:
 * most settings have no scheduled window at all, and this view still has
 * no control to create one (§8.2's write-side gap, unchanged).
 */
export function settingValidityLabel(setting: Setting): string | null {
  if (setting.validFrom && setting.validTo) {
    return de.settings.validity.range(formatDate(setting.validFrom), formatDate(setting.validTo))
  }
  if (setting.validFrom) return de.settings.validity.from(formatDate(setting.validFrom))
  if (setting.validTo) return de.settings.validity.until(formatDate(setting.validTo))
  return null
}

export interface SettingFormValues {
  /**
   * For an ordinary setting: the value itself, pre-filled. For a secret
   * one: the write-only "new value" field, which starts empty — there is
   * nothing to pre-fill it with — and stays empty unless the operator
   * types a replacement. `''` here means exactly what an admin PUT that
   * omits `value` means: leave the stored secret alone.
   */
  value: string
  description: string
  forFrontend: boolean
}

export function initialFormValues(setting: Setting): SettingFormValues {
  return {
    value: setting.isSecret ? '' : setting.value,
    description: setting.description,
    forFrontend: setting.forFrontend,
  }
}

/**
 * §8.1: `for_frontend` and `is_secret` together is a contradiction the
 * server refuses against the merged result (writeSetting, service.ts) — it
 * would mean sending a sealed credential to every browser. This view has no
 * control to change `isSecret` (see the module note below), so the only way
 * the conflict can arise here is an already-secret setting; checked here so
 * the toggle can be disabled, with a reason, before the operator ever tries.
 */
export function forFrontendBlockedBySecret(setting: Setting): boolean {
  return setting.isSecret
}

/**
 * Only the fields the operator actually touched — never resending an
 * unmodified one. `writeSetting` (service.ts) merges an omitted field with
 * whatever is already stored; resending the old value as if it were fresh
 * input would not be wrong exactly, but it would make every save touch
 * fields the operator never looked at, and it would make "what changed"
 * (`settingConfirmationLines` below) claim a change that did not happen.
 *
 * Deliberately never sets `isSecret`: this view offers no control for it.
 * `is_secret` is fixed per key by the seed migration (design §8.3) — this
 * is where an operator fills in Microsoft and openship credentials, not
 * where the table's own shape gets restructured. That is also why the
 * write side's secret-downgrade refusal (§writeSetting: turning `isSecret`
 * from true to false without a new value) can never be triggered from this
 * screen — there is no path here that sends `isSecret` at all.
 */
export function buildSettingChanges(setting: Setting, form: SettingFormValues): WriteSettingBody {
  const changes: WriteSettingBody = {}
  if (setting.isSecret) {
    if (form.value !== '') changes.value = form.value
  } else if (form.value !== setting.value) {
    changes.value = form.value
  }
  if (form.description !== setting.description) changes.description = form.description
  if (form.forFrontend !== setting.forFrontend) changes.forFrontend = form.forFrontend
  return changes
}

export function hasSettingChanges(changes: WriteSettingBody): boolean {
  return Object.keys(changes).length > 0
}

/**
 * Whether the save button may be pressed at all: something must actually
 * be different, and — belt and braces alongside `forFrontendBlockedBySecret`
 * disabling the toggle itself — a secret may never be submitted with
 * `forFrontend` on.
 */
export function canSaveSetting(setting: Setting, form: SettingFormValues): boolean {
  if (setting.isSecret && form.forFrontend) return false
  return hasSettingChanges(buildSettingChanges(setting, form))
}

/**
 * §7's confirmation, built from exactly what `buildSettingChanges` will
 * send — so the sentence can never claim a change the request does not
 * actually make. One line per changed field; a secret's new value is never
 * echoed (design §8.1 — the whole point of "hinterlegt" is that reading a
 * secret back defeats not storing it visibly).
 */
export function settingConfirmationLines(setting: Setting, changes: WriteSettingBody): string[] {
  const lines: string[] = []
  if (changes.value !== undefined) {
    lines.push(setting.isSecret ? de.settings.confirm.newSecretValue : de.settings.confirm.newValue(changes.value))
  }
  if (changes.description !== undefined) {
    lines.push(de.settings.confirm.newDescription(changes.description))
  }
  if (changes.forFrontend !== undefined) {
    lines.push(changes.forFrontend ? de.settings.confirm.forFrontendOn : de.settings.confirm.forFrontendOff)
  }
  return lines
}

/**
 * §8's "clearing a secret is a separate, explicit action, not saving an
 * empty field": its own confirmation, never folded into
 * `settingConfirmationLines`, and its own PUT body (`{ value: '' }`, sent
 * on its own, with nothing else) — a distinct action, not "save with a
 * blank value field".
 */
export function clearSecretChanges(): WriteSettingBody {
  return { value: '' }
}
